/* =============================================================================
   Application domain data
   -----------------------------------------------------------------------------
   The mockups are only convincing if the numbers agree with each other, so this
   file is derived from the design system's own fixtures rather than invented
   alongside them. Three rules hold throughout, and every figure below is checked
   against them:

     1. A project's investor participations sum EXACTLY to `project.contributed`.
        The investors' shares sum to `100 - project.ownershipShare`, because the
        builder retains the rest. A dashboard that shows a participation total
        which disagrees with its own project card is the fastest way to lose a
        client's confidence in a demo.
     2. A project's per-phase actuals sum EXACTLY to `project.actual`. This is why
        the schedules live here for all three projects rather than only for
        Lakeview, and why expenses are GENERATED from the phase actuals instead
        of hand-written: hand-written rows drift, generated rows reconcile by
        construction.
     3. Only content that has been through approval is `published`. The internal
        screens show the full queue; the portal shows the published subset. That
        is the document's central rule (§11: "no content becomes visible to the
        investor merely because it was entered") and the demo must demonstrate it
        rather than assert it.
   ============================================================================= */

import { PROJECTS, SCHEDULE, type Project, type SchedulePhase } from 'escrowbuild-ui';

/* -----------------------------------------------------------------------------
   One import site
   -----------------------------------------------------------------------------
   A page imports everything it renders from `@/data/domain` — its own records and
   the design system's fixtures alike. Re-exporting them here rather than letting
   each page reach into the package means a page has one import line and one
   place to look, and when the fixtures move behind a real API this module is the
   only file that changes.
   -------------------------------------------------------------------------- */

export {
  CASH_FLOW,
  CONTRACTORS,
  CONTRACTOR_OPTIONS,
  INSPECTIONS_BY_MONTH,
  LABOUR_BY_TRADE,
  NOTIFICATIONS,
  PEOPLE,
  PHASE_BUDGET,
  PROJECTS,
  SCHEDULE,
  SHARE_BY_PROJECT,
  SPEND_BY_MONTH,
  UPDATES,
} from 'escrowbuild-ui';

export type {
  ContractorRow,
  NotificationItem,
  Project,
  ProjectUpdate,
  Row,
  SchedulePhase,
} from 'escrowbuild-ui';

/* ---- People and capital -------------------------------------------------- */

export interface Investor {
  id: string;
  name: string;
  kind: 'individual' | 'fund' | 'trust';
  email: string;
  phone: string;
  city: string;
  joinedAt: string;
  status: 'active' | 'prospect' | 'closed';
  /** Reference tax identifier, as the finance team records it. */
  taxId: string;
}

export interface Participation {
  id: string;
  investorId: string;
  projectId: string;
  /** Percent of the whole project. Participants of one project sum to 100 − ownershipShare. */
  sharePercent: number;
  committed: number;
  contributed: number;
  /** Capital calls issued and settled, for the funding schedule. */
  callsIssued: number;
  callsSettled: number;
  joinedAt: string;
  documents: number;
  status: 'confirmed' | 'pending' | 'draft';
}

export const INVESTORS: Investor[] = [
  {
    id: 'marlow',
    name: 'Marlow Capital',
    kind: 'fund',
    email: 'deals@marlowcapital.com',
    phone: '+1 (512) 555-0148',
    city: 'Austin, TX',
    joinedAt: '2024-02-11',
    status: 'active',
    taxId: '27-4481209',
  },
  {
    id: 'prado',
    name: 'Helena Prado',
    kind: 'individual',
    email: 'helena.prado@pmail.com',
    phone: '+1 (512) 555-0192',
    city: 'Lakeway, TX',
    joinedAt: '2024-05-30',
    status: 'active',
    taxId: '—',
  },
  {
    id: 'whitfield',
    name: 'R. & C. Whitfield',
    kind: 'trust',
    email: 'trustee@whitfieldfamilytrust.com',
    phone: '+1 (737) 555-0113',
    city: 'West Lake Hills, TX',
    joinedAt: '2023-11-02',
    status: 'active',
    taxId: '74-2201938',
  },
  {
    id: 'sunfield',
    name: 'Sunfield Holdings',
    kind: 'fund',
    email: 'acquisitions@sunfieldholdings.com',
    phone: '+1 (512) 555-0177',
    city: 'Dallas, TX',
    joinedAt: '2023-08-19',
    status: 'active',
    taxId: '31-9920471',
  },
  {
    id: 'okonkwo',
    name: 'T. Okonkwo',
    kind: 'individual',
    email: 't.okonkwo@pmail.com',
    phone: '+1 (512) 555-0164',
    city: 'Round Rock, TX',
    joinedAt: '2025-01-14',
    status: 'active',
    taxId: '—',
  },
  {
    id: 'northgate',
    name: 'Northgate Trust',
    kind: 'trust',
    email: 'office@northgatetrust.com',
    phone: '+1 (469) 555-0121',
    city: 'Plano, TX',
    joinedAt: '2024-09-08',
    status: 'active',
    taxId: '82-7710395',
  },
  {
    id: 'vantage',
    name: 'Vantage Ridge Partners',
    kind: 'fund',
    email: 'ir@vantageridge.com',
    phone: '+1 (512) 555-0209',
    city: 'Houston, TX',
    joinedAt: '2026-07-22',
    status: 'prospect',
    taxId: '88-1029384',
  },
];

/* Shares sum to 66 / 78 / 82 — one minus each project's retained ownership —
   and contributed sums to each project's `contributed` exactly. */
export const PARTICIPATIONS: Participation[] = [
  // Lakeview — 66% to investors, 1,240,000 contributed
  { id: 'p-lv-marlow', investorId: 'marlow', projectId: 'lakeview', sharePercent: 26, committed: 500_000, contributed: 480_000, callsIssued: 5, callsSettled: 4, joinedAt: '2025-03-04', documents: 9, status: 'confirmed' },
  { id: 'p-lv-prado', investorId: 'prado', projectId: 'lakeview', sharePercent: 18, committed: 350_000, contributed: 330_000, callsIssued: 5, callsSettled: 4, joinedAt: '2025-03-18', documents: 7, status: 'confirmed' },
  { id: 'p-lv-whitfield', investorId: 'whitfield', projectId: 'lakeview', sharePercent: 12, committed: 240_000, contributed: 230_000, callsIssued: 5, callsSettled: 4, joinedAt: '2025-04-02', documents: 6, status: 'confirmed' },
  { id: 'p-lv-sunfield', investorId: 'sunfield', projectId: 'lakeview', sharePercent: 10, committed: 210_000, contributed: 200_000, callsIssued: 5, callsSettled: 4, joinedAt: '2025-04-22', documents: 6, status: 'confirmed' },
  // Maple Court — 78%, 860,000
  { id: 'p-mc-marlow', investorId: 'marlow', projectId: 'maple-court', sharePercent: 30, committed: 350_000, contributed: 340_000, callsIssued: 4, callsSettled: 3, joinedAt: '2025-06-09', documents: 8, status: 'confirmed' },
  { id: 'p-mc-prado', investorId: 'prado', projectId: 'maple-court', sharePercent: 24, committed: 260_000, contributed: 255_000, callsIssued: 4, callsSettled: 3, joinedAt: '2025-06-20', documents: 6, status: 'confirmed' },
  { id: 'p-mc-okonkwo', investorId: 'okonkwo', projectId: 'maple-court', sharePercent: 14, committed: 160_000, contributed: 155_000, callsIssued: 4, callsSettled: 3, joinedAt: '2025-07-11', documents: 5, status: 'confirmed' },
  { id: 'p-mc-northgate', investorId: 'northgate', projectId: 'maple-court', sharePercent: 10, committed: 115_000, contributed: 110_000, callsIssued: 4, callsSettled: 3, joinedAt: '2025-07-28', documents: 5, status: 'pending' },
  // Birch Hollow — 82%, 410,000
  { id: 'p-bh-sunfield', investorId: 'sunfield', projectId: 'birch-hollow', sharePercent: 34, committed: 180_000, contributed: 175_000, callsIssued: 3, callsSettled: 1, joinedAt: '2026-02-17', documents: 6, status: 'confirmed' },
  { id: 'p-bh-northgate', investorId: 'northgate', projectId: 'birch-hollow', sharePercent: 28, committed: 140_000, contributed: 135_000, callsIssued: 3, callsSettled: 1, joinedAt: '2026-03-02', documents: 5, status: 'confirmed' },
  { id: 'p-bh-whitfield', investorId: 'whitfield', projectId: 'birch-hollow', sharePercent: 20, committed: 105_000, contributed: 100_000, callsIssued: 3, callsSettled: 1, joinedAt: '2026-03-19', documents: 4, status: 'confirmed' },
  // A prospect on the opportunity pipeline has no participation yet, by design.
  { id: 'p-mc-vantage', investorId: 'vantage', projectId: 'maple-court', sharePercent: 0, committed: 0, contributed: 0, callsIssued: 0, callsSettled: 0, joinedAt: '2026-08-30', documents: 2, status: 'draft' },
];

/* ---- Schedule ----------------------------------------------------------- */

/**
 * The reference phases of the document's §7, in order. Every project's schedule
 * is a composition of these, because the platform must not impose one rigid
 * sequence — phases overlap, and the schedules below show them overlapping.
 */
/**
 * Deliberately NOT `as const`.
 *
 * `as const` narrows every `id` to a nine-member literal union, which reads like
 * a free typo check but is not: the phase id does not stop at these nine values.
 * Every other record in this file carries a phase id as a plain `string` — an
 * expense's `phaseId`, an evidence item's, a document's — because the document's
 * §7 is explicit that the phase dictionary is configurable per company and per
 * development. So the union was a *narrower* type than the model it claimed to
 * describe, and consumers that build a lookup (`new Map(PHASE_SEQUENCE.map(…))`)
 * then rejected perfectly valid ids.
 *
 * A type that forces callers to cast in order to use the data correctly is
 * describing something the data is not. `string` is the honest type here.
 */
export const PHASE_SEQUENCE: { id: string; name: string }[] = [
  { id: 'planning', name: 'Planning and pre-construction' },
  { id: 'acquisition', name: 'Acquisition and mobilization' },
  { id: 'demolition', name: 'Demolition and site prep' },
  { id: 'foundation', name: 'Foundation' },
  { id: 'framing', name: 'Framing and roofing' },
  { id: 'utilities', name: 'Utilities and enclosure' },
  { id: 'finishes', name: 'Finishes' },
  { id: 'landscaping', name: 'Landscaping' },
  { id: 'completion', name: 'Completion and sale' },
];

/** Lakeview's schedule is the design system's own fixture — actuals sum to 1,084,500. */
const LAKEVIEW_SCHEDULE: SchedulePhase[] = SCHEDULE;

/** Maple Court — actuals sum to 702,000. Framing runs alongside finishes prep. */
const MAPLE_COURT_SCHEDULE: SchedulePhase[] = [
  { id: 'planning', name: 'Planning and pre-construction', plannedStart: 0, plannedWidth: 24, actualStart: 0, actualWidth: 25, status: 'completed', budgeted: 88_000, actual: 76_200 },
  { id: 'acquisition', name: 'Acquisition and mobilization', plannedStart: 20, plannedWidth: 14, actualStart: 21, actualWidth: 15, status: 'completed', budgeted: 215_000, actual: 208_400 },
  { id: 'demolition', name: 'Demolition and site prep', plannedStart: 32, plannedWidth: 12, actualStart: 33, actualWidth: 11, status: 'completed', budgeted: 98_000, actual: 94_100 },
  { id: 'foundation', name: 'Foundation', plannedStart: 42, plannedWidth: 18, actualStart: 41, actualWidth: 19, status: 'completed', budgeted: 205_000, actual: 198_300 },
  { id: 'framing', name: 'Framing and roofing', plannedStart: 58, plannedWidth: 26, actualStart: 57, actualWidth: 16, status: 'in-progress', budgeted: 262_000, actual: 125_000 },
  { id: 'utilities', name: 'Utilities and enclosure', plannedStart: 74, plannedWidth: 28, actualStart: null, actualWidth: null, status: 'planned', budgeted: 198_000, actual: 0 },
  { id: 'finishes', name: 'Finishes', plannedStart: 86, plannedWidth: 32, actualStart: null, actualWidth: null, status: 'planned', budgeted: 288_000, actual: 0 },
  { id: 'landscaping', name: 'Landscaping', plannedStart: 96, plannedWidth: 14, actualStart: null, actualWidth: null, status: 'planned', budgeted: 92_000, actual: 0 },
  { id: 'completion', name: 'Completion and sale', plannedStart: 102, plannedWidth: 10, actualStart: null, actualWidth: null, status: 'planned', budgeted: 0, actual: 0 },
];

/** Birch Hollow — actuals sum to 96,000. Foundation is next, not yet started. */
const BIRCH_HOLLOW_SCHEDULE: SchedulePhase[] = [
  { id: 'planning', name: 'Planning and pre-construction', plannedStart: 0, plannedWidth: 28, actualStart: 0, actualWidth: 30, status: 'completed', budgeted: 46_000, actual: 41_100 },
  { id: 'acquisition', name: 'Acquisition and mobilization', plannedStart: 26, plannedWidth: 20, actualStart: 25, actualWidth: 17, status: 'in-progress', budgeted: 196_000, actual: 54_900 },
  { id: 'demolition', name: 'Demolition and site prep', plannedStart: 42, plannedWidth: 12, actualStart: null, actualWidth: null, status: 'planned', budgeted: 72_000, actual: 0 },
  { id: 'foundation', name: 'Foundation', plannedStart: 52, plannedWidth: 20, actualStart: null, actualWidth: null, status: 'planned', budgeted: 214_000, actual: 0 },
  { id: 'framing', name: 'Framing and roofing', plannedStart: 70, plannedWidth: 24, actualStart: null, actualWidth: null, status: 'planned', budgeted: 268_000, actual: 0 },
  { id: 'utilities', name: 'Utilities and enclosure', plannedStart: 86, plannedWidth: 26, actualStart: null, actualWidth: null, status: 'planned', budgeted: 204_000, actual: 0 },
  { id: 'finishes', name: 'Finishes', plannedStart: 100, plannedWidth: 30, actualStart: null, actualWidth: null, status: 'planned', budgeted: 296_000, actual: 0 },
  { id: 'landscaping', name: 'Landscaping', plannedStart: 116, plannedWidth: 14, actualStart: null, actualWidth: null, status: 'planned', budgeted: 88_000, actual: 0 },
  { id: 'completion', name: 'Completion and sale', plannedStart: 122, plannedWidth: 10, actualStart: null, actualWidth: null, status: 'planned', budgeted: 0, actual: 0 },
];

export const SCHEDULES: Record<string, SchedulePhase[]> = {
  lakeview: LAKEVIEW_SCHEDULE,
  'maple-court': MAPLE_COURT_SCHEDULE,
  'birch-hollow': BIRCH_HOLLOW_SCHEDULE,
};

/* ---- Expenses ------------------------------------------------------------ */

export type ExpenseCategory =
  | 'Materials'
  | 'Labour'
  | 'Equipment'
  | 'Permits and fees'
  | 'Professional services'
  | 'Land and acquisition'
  | 'Contingency';

/**
 * Which slice of a phase's spend a line is, as a CODE rather than as the words
 * "initial draw".
 *
 * The description below is the English sentence a reader sees, and it is built
 * from the phase name and one of these. Carrying the code as well is what lets a
 * reader in another language be shown the same line in their own words: the
 * sentence cannot be translated by editing it, because half of it is the phase
 * dictionary, and the words of a sentence are not a stable key.
 */
export type ExpenseDraw = 'initial' | 'progress' | 'materials' | 'retention' | 'final';

/**
 * The English words for each draw, exported because the sentence is rebuilt at
 * render time and this is the half of it that is not the phase dictionary.
 */
export const DRAW_LABELS: Record<ExpenseDraw, string> = {
  initial: 'initial draw',
  progress: 'progress billing',
  materials: 'materials release',
  retention: 'retention release',
  final: 'final settlement',
};

/** The slice order, so `index` and the code name the same line. */
const DRAW_ORDER: ExpenseDraw[] = ['initial', 'progress', 'materials', 'retention', 'final'];

export interface Expense {
  id: string;
  projectId: string;
  phaseId: string;
  date: string;
  vendorId: string;
  vendorName: string;
  category: ExpenseCategory;
  /** Which slice of the phase this line is — see `ExpenseDraw`. */
  draw: ExpenseDraw;
  description: string;
  amount: number;
  /** Which side of the forecast the line is on, and the variance it explains. */
  forecast: number;
  status: 'approved' | 'in-review' | 'disputed';
  evidence: number;
  /** Delivery can lag payment — the document's §9 framing example. */
  delivery: 'delivered' | 'partial' | 'pending' | 'not-applicable';
}

const CATEGORY_FOR_PHASE: Record<string, ExpenseCategory> = {
  planning: 'Professional services',
  acquisition: 'Land and acquisition',
  demolition: 'Equipment',
  foundation: 'Materials',
  framing: 'Materials',
  utilities: 'Materials',
  finishes: 'Materials',
  landscaping: 'Labour',
  completion: 'Professional services',
};

const VENDOR_NAMES = [
  'Bastrop Building Supply',
  'Kyle Concrete Works',
  'Round Rock Electric',
  'Cedar Park Plumbing Co.',
  'Pflugerville Drywall',
  'Leander Roofing & Sheet Metal',
  'Hutto Trim and Millwork',
  'Georgetown Excavation',
  'Buda Insulation Partners',
  'Manor Glass and Glazing',
  'Sunset Valley Landscaping',
  'Taylor Steel Supply',
  'Dripping Springs Cabinetry',
  'Lakeway Tile and Stone',
  'Elgin Haulage',
  'Lockhart Masonry',
];

/**
 * Expenses are generated from each phase's actual, split by a fixed weight
 * vector and rounded so the last line absorbs the remainder. This is deliberate:
 * a table of hand-authored rows would not sum to the phase total shown two
 * screens away, and the mismatch would be visible in the demo. Here it cannot
 * drift, because the phases are the source and the rows are derived.
 */
function buildExpenses(): Expense[] {
  const rows: Expense[] = [];
  const weights = [0.34, 0.27, 0.19, 0.13, 0.07];

  for (const project of PROJECTS) {
    const schedule = SCHEDULES[project.id] ?? [];
    let seq = 0;

    for (const phase of schedule) {
      if (phase.actual <= 0) continue;

      const isOpen = phase.status === 'in-progress';
      const splits = isOpen ? weights.slice(0, 3) : weights;
      let allocated = 0;

      splits.forEach((weight, index) => {
        const isLast = index === splits.length - 1;
        // The final line absorbs the rounding remainder, so the sum is exact.
        const amount = isLast
          ? phase.actual - allocated
          : Math.round((phase.actual * weight) / 100) * 100;
        allocated += amount;
        if (amount <= 0) return;

        seq += 1;
        const vendorName = VENDOR_NAMES[(seq * 5 + index * 3) % VENDOR_NAMES.length];
        const month = String(((phase.actualStart ?? 0) + index * 2) % 12 + 1).padStart(2, '0');

        const draw = DRAW_ORDER[index] ?? 'final';

        rows.push({
          id: `exp-${project.id}-${seq}`,
          projectId: project.id,
          phaseId: phase.id,
          date: `2026-${month}-${String(((seq * 7) % 26) + 2).padStart(2, '0')}`,
          vendorId: `con-${((seq * 5 + index * 3) % 84) + 1}`,
          vendorName,
          category: CATEGORY_FOR_PHASE[phase.id] ?? 'Materials',
          draw,
          description: `${phase.name} — ${DRAW_LABELS[draw]}`,
          amount,
          forecast: Math.round((amount * (1 + (index % 2 === 0 ? 0.045 : -0.03))) / 100) * 100,
          status: isOpen && index === splits.length - 1 ? 'in-review' : 'approved',
          evidence: 1 + ((seq + index) % 4),
          delivery: phase.id === 'acquisition' ? 'pending' : index === splits.length - 1 ? 'partial' : 'delivered',
        });
      });
    }
  }

  return rows;
}

export const EXPENSES: Expense[] = buildExpenses();

/* ---- Milestones ---------------------------------------------------------- */

export interface Milestone {
  id: string;
  projectId: string;
  phaseId: string;
  title: string;
  date: string;
  status: 'reached' | 'upcoming';
  /** True when an investor-facing notification was sent for it. */
  notified: boolean;
}

export const MILESTONES: Milestone[] = [
  { id: 'm-lv-1', projectId: 'lakeview', phaseId: 'framing', title: 'Framing topped out', date: '2026-07-24', status: 'reached', notified: true },
  { id: 'm-lv-2', projectId: 'lakeview', phaseId: 'framing', title: 'Roofing finished and water test passed', date: '2026-08-21', status: 'reached', notified: true },
  { id: 'm-lv-3', projectId: 'lakeview', phaseId: 'framing', title: 'Municipal inspection passed, no findings', date: '2026-09-18', status: 'reached', notified: true },
  { id: 'm-lv-4', projectId: 'lakeview', phaseId: 'utilities', title: 'Rough-in complete on both floors', date: '2026-10-16', status: 'upcoming', notified: false },
  { id: 'm-lv-5', projectId: 'lakeview', phaseId: 'utilities', title: 'Enclosure and weather-tight', date: '2026-11-06', status: 'upcoming', notified: false },
  { id: 'm-lv-6', projectId: 'lakeview', phaseId: 'finishes', title: 'Cabinet installation begins', date: '2026-12-04', status: 'upcoming', notified: false },
  { id: 'm-lv-7', projectId: 'lakeview', phaseId: 'completion', title: 'Final inspection and certificate of occupancy', date: '2027-01-22', status: 'upcoming', notified: false },
  { id: 'm-mc-1', projectId: 'maple-court', phaseId: 'foundation', title: 'Foundation poured and cured', date: '2026-08-14', status: 'reached', notified: true },
  { id: 'm-mc-2', projectId: 'maple-court', phaseId: 'framing', title: 'Second-floor deck set', date: '2026-09-30', status: 'reached', notified: false },
  { id: 'm-mc-3', projectId: 'maple-court', phaseId: 'framing', title: 'Trusses set, roof dried in', date: '2026-10-28', status: 'upcoming', notified: false },
  { id: 'm-bh-1', projectId: 'birch-hollow', phaseId: 'acquisition', title: 'Site mobilized, erosion controls in place', date: '2026-09-11', status: 'reached', notified: false },
  { id: 'm-bh-2', projectId: 'birch-hollow', phaseId: 'demolition', title: 'Existing structure cleared', date: '2026-11-13', status: 'upcoming', notified: false },
];

/* ---- Evidence: photos and documents -------------------------------------- */

export interface EvidenceItem {
  id: string;
  projectId: string;
  phaseId: string;
  kind: 'photo' | 'video' | 'note';
  /**
   * The caption's own id, carried beside the caption itself.
   *
   * A caption is reused across every development that reaches its phase, so the
   * words cannot be the key — one edit to a sentence would orphan its
   * translation — and neither can the item's position, because the same wording
   * moves as phases are added. The id travels with the sentence instead.
   */
  captionId: string;
  caption: string;
  date: string;
  capturedBy: string;
  /** The doc's §11 rule — captured internally, not necessarily investor-visible. */
  published: boolean;
}

export interface DocumentRecord {
  id: string;
  projectId: string;
  phaseId: string | null;
  name: string;
  type: 'invoice' | 'receipt' | 'contract' | 'permit' | 'quote' | 'report' | 'inspection';
  date: string;
  size: string;
  uploadedBy: string;
  /** The document's own review state, separate from whether it is published. */
  status: 'verified' | 'in-review' | 'rejected';
  published: boolean;
}

/**
 * What a phase's photographs are of, each carrying the id its translation is
 * keyed by. The ids are stable names for the subject — a plan set, a rebar
 * inspection — and not a slug of the English, so editing the sentence does not
 * move the key.
 */
const CAPTIONS: Record<string, { id: string; text: string }[]> = {
  planning: [
    { id: 'plan-set-issued', text: 'Completed plan set issued for permit' },
    { id: 'survey-stakes', text: 'Site survey stakes in place' },
    { id: 'utility-locates', text: 'Utility locate markings' },
  ],
  acquisition: [
    { id: 'lot-cleared-staked', text: 'Lot cleared and staked' },
    { id: 'temp-power', text: 'Temporary power connected' },
    { id: 'initial-site-photos', text: 'Initial site photos for the record' },
  ],
  demolition: [
    { id: 'structure-before-demolition', text: 'Existing structure before demolition' },
    { id: 'demolition-in-progress', text: 'Demolition in progress' },
    { id: 'site-cleared-graded', text: 'Site cleared and graded' },
  ],
  foundation: [
    { id: 'excavation-to-grade', text: 'Excavation to grade' },
    { id: 'rebar-cage-inspected', text: 'Rebar cage inspected before pour' },
    { id: 'footings-poured', text: 'Footings poured' },
    { id: 'slab-cured', text: 'Slab cured and ready' },
  ],
  framing: [
    { id: 'first-floor-framing', text: 'First-floor framing' },
    { id: 'second-floor-deck', text: 'Second-floor deck' },
    { id: 'trusses-delivered', text: 'Trusses delivered' },
    { id: 'roof-sheathing', text: 'Roof sheathing complete' },
    { id: 'roof-water-test', text: 'Water test on roof' },
  ],
  utilities: [
    { id: 'rough-in-electrical', text: 'Rough-in electrical' },
    { id: 'rough-in-plumbing', text: 'Rough-in plumbing' },
    { id: 'hvac-ductwork', text: 'HVAC ductwork' },
    { id: 'enclosure-wrap', text: 'Enclosure wrap' },
  ],
  finishes: [
    { id: 'drywall-hung-taped', text: 'Drywall hung and taped' },
    { id: 'cabinet-install-started', text: 'Cabinet install started' },
    { id: 'paint-and-trim', text: 'Paint and trim' },
    { id: 'flooring-laid', text: 'Flooring laid' },
  ],
  landscaping: [
    { id: 'grading-and-drainage', text: 'Grading and drainage' },
    { id: 'planting-plan-executed', text: 'Planting plan executed' },
    { id: 'driveway-poured', text: 'Driveway poured' },
  ],
  completion: [
    { id: 'final-walkthrough', text: 'Final walkthrough' },
    { id: 'punch-list-cleared', text: 'Punch list cleared' },
    { id: 'listing-preparation', text: 'Listing preparation' },
  ],
};

/**
 * The Gantt axis is a percentage, but the developments run about two years, so a
 * phase position becomes a real date by anchoring the axis end at the projected
 * sale and the start 24 months earlier.
 *
 * This replaced a version that wrapped the position modulo twelve, which dated a
 * 2025 foundation pour to December 2026 and put a Foundation phase's photos after
 * its own completion. Incoherent dates are worse than absent ones on a screen
 * whose entire purpose is provenance — an investor checking a photo against a
 * phase is exactly the review this data exists to support.
 */
const AXIS_MONTHS = 24;

function axisDate(project: Project, position: number): string {
  const start = new Date(`${project.projectedSale}T00:00:00`);
  start.setMonth(start.getMonth() - AXIS_MONTHS);

  const clamped = Math.min(Math.max(position, 0), 100);
  const months = (clamped / 100) * AXIS_MONTHS;

  const date = new Date(start);
  date.setMonth(date.getMonth() + Math.floor(months));
  date.setDate(1 + Math.round((months % 1) * 27));
  return date.toISOString().slice(0, 10);
}

function buildEvidence(): EvidenceItem[] {
  const out: EvidenceItem[] = [];
  let seq = 0;

  for (const project of PROJECTS) {
    for (const phase of SCHEDULES[project.id] ?? []) {
      const captions = CAPTIONS[phase.id] ?? [];
      const reached = phase.status === 'completed' || phase.status === 'in-progress';
      if (!reached) continue;

      const anchor = phase.actualStart ?? phase.plannedStart;

      captions.forEach((caption, index) => {
        seq += 1;
        out.push({
          id: `ev-${project.id}-${seq}`,
          projectId: project.id,
          phaseId: phase.id,
          kind: 'photo',
          captionId: caption.id,
          caption: caption.text,
          // Captions spread across the phase rather than sharing one instant, so
          // a phase's evidence reads as a period and not as a single visit.
          date: axisDate(project, anchor + index * 4),
          capturedBy: ['M. Alvarez', 'J. Ferris', 'D. Nakamura', 'P. Osei'][seq % 4],
          // Roughly two thirds are cleared for the portal, so the demo shows a
          // real difference between the internal library and the investor view.
          published: seq % 3 !== 0,
        });
      });
    }
  }

  return out;
}

export const EVIDENCE: EvidenceItem[] = buildEvidence();

export const DOCUMENTS: DocumentRecord[] = [
  { id: 'doc-1', projectId: 'lakeview', phaseId: 'planning', name: 'Permit set — architectural drawings', type: 'permit', date: '2025-04-18', size: '14.2 MB', uploadedBy: 'R. Campos', status: 'verified', published: true },
  { id: 'doc-2', projectId: 'lakeview', phaseId: 'planning', name: 'Base budget Rev. C', type: 'report', date: '2025-05-02', size: '820 KB', uploadedBy: 'S. Iyer', status: 'verified', published: true },
  { id: 'doc-3', projectId: 'lakeview', phaseId: 'acquisition', name: 'Land purchase agreement', type: 'contract', date: '2025-06-11', size: '3.1 MB', uploadedBy: 'R. Campos', status: 'verified', published: true },
  { id: 'doc-4', projectId: 'lakeview', phaseId: 'acquisition', name: 'Title policy', type: 'contract', date: '2025-06-14', size: '1.4 MB', uploadedBy: 'S. Iyer', status: 'verified', published: true },
  { id: 'doc-5', projectId: 'lakeview', phaseId: 'demolition', name: 'Demolition permit', type: 'permit', date: '2025-07-09', size: '410 KB', uploadedBy: 'R. Campos', status: 'verified', published: true },
  { id: 'doc-6', projectId: 'lakeview', phaseId: 'foundation', name: 'Concrete supplier invoice 4471', type: 'invoice', date: '2025-09-03', size: '96 KB', uploadedBy: 'L. Beaulieu', status: 'verified', published: true },
  { id: 'doc-7', projectId: 'lakeview', phaseId: 'foundation', name: 'Rebar inspection report', type: 'inspection', date: '2025-09-12', size: '220 KB', uploadedBy: 'L. Beaulieu', status: 'verified', published: true },
  { id: 'doc-8', projectId: 'lakeview', phaseId: 'framing', name: 'Truss engineering package', type: 'report', date: '2026-05-28', size: '6.8 MB', uploadedBy: 'M. Alvarez', status: 'verified', published: true },
  { id: 'doc-9', projectId: 'lakeview', phaseId: 'framing', name: 'Municipal framing inspection — passed', type: 'inspection', date: '2026-09-18', size: '180 KB', uploadedBy: 'M. Alvarez', status: 'verified', published: true },
  { id: 'doc-10', projectId: 'lakeview', phaseId: 'utilities', name: 'Electrical rough-in quote — revised', type: 'quote', date: '2026-09-26', size: '140 KB', uploadedBy: 'L. Beaulieu', status: 'in-review', published: false },
  { id: 'doc-11', projectId: 'lakeview', phaseId: 'utilities', name: 'HVAC equipment invoice (pending delivery)', type: 'invoice', date: '2026-09-21', size: '205 KB', uploadedBy: 'L. Beaulieu', status: 'in-review', published: false },
  { id: 'doc-12', projectId: 'lakeview', phaseId: 'finishes', name: 'Cabinet shop drawings', type: 'report', date: '2026-09-15', size: '9.4 MB', uploadedBy: 'D. Nakamura', status: 'in-review', published: false },
  { id: 'doc-13', projectId: 'maple-court', phaseId: 'planning', name: 'Permit set — architectural drawings', type: 'permit', date: '2025-08-04', size: '12.9 MB', uploadedBy: 'R. Campos', status: 'verified', published: true },
  { id: 'doc-14', projectId: 'maple-court', phaseId: 'acquisition', name: 'Land purchase agreement', type: 'contract', date: '2025-09-22', size: '2.8 MB', uploadedBy: 'R. Campos', status: 'verified', published: true },
  { id: 'doc-15', projectId: 'maple-court', phaseId: 'foundation', name: 'Foundation inspection — passed', type: 'inspection', date: '2026-08-14', size: '175 KB', uploadedBy: 'P. Osei', status: 'verified', published: true },
  { id: 'doc-16', projectId: 'maple-court', phaseId: 'framing', name: 'Lumber package invoice 8830', type: 'invoice', date: '2026-09-08', size: '310 KB', uploadedBy: 'L. Beaulieu', status: 'verified', published: false },
  { id: 'doc-17', projectId: 'birch-hollow', phaseId: 'planning', name: 'Feasibility study and pro forma', type: 'report', date: '2026-04-30', size: '5.2 MB', uploadedBy: 'S. Iyer', status: 'verified', published: true },
  { id: 'doc-18', projectId: 'birch-hollow', phaseId: 'acquisition', name: 'Purchase agreement — 41 Birch Hollow', type: 'contract', date: '2026-06-19', size: '3.4 MB', uploadedBy: 'R. Campos', status: 'verified', published: true },
  { id: 'doc-19', projectId: 'birch-hollow', phaseId: 'acquisition', name: 'Erosion control plan', type: 'report', date: '2026-08-27', size: '640 KB', uploadedBy: 'P. Osei', status: 'in-review', published: false },
  { id: 'doc-20', projectId: 'birch-hollow', phaseId: 'acquisition', name: 'Mobilization cost breakdown', type: 'report', date: '2026-09-02', size: '290 KB', uploadedBy: 'L. Beaulieu', status: 'rejected', published: false },
];

/* ---- Publication queue — §11's register / review / approve / publish ---- */

export type PublicationStatus = 'draft' | 'in-review' | 'approved' | 'published';

export interface Publication {
  id: string;
  projectId: string;
  title: string;
  period: string;
  status: PublicationStatus;
  preparedBy: string;
  preparedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  /** Which evidence and documents this update would carry to the investor. */
  photos: number;
  documents: number;
  /** §13 — an update can be published quietly, or notified actively. */
  notify: boolean;
  /** Money figures below are the ones the investor would be shown. */
  spendToDate: number;
  variance: number;
  body: string;
}

export const PUBLICATIONS: Publication[] = [
  {
    id: 'pub-lv-09',
    projectId: 'lakeview',
    title: 'Framing complete and inspection approved',
    period: 'September 2026',
    status: 'published',
    preparedBy: 'M. Alvarez',
    preparedAt: '2026-09-16',
    reviewedBy: 'S. Iyer',
    reviewedAt: '2026-09-17',
    approvedBy: 'R. Campos',
    approvedAt: '2026-09-18',
    publishedAt: '2026-09-18',
    photos: 12,
    documents: 3,
    notify: true,
    spendToDate: 1_084_500,
    variance: -28_400,
    body: 'Structure and roofing closed on schedule. The municipal inspection passed with no findings, and the phase settled $28,400 under the figure published in June.',
  },
  {
    id: 'pub-lv-08',
    projectId: 'lakeview',
    title: 'Electrical and plumbing started in parallel',
    period: 'Early September 2026',
    status: 'published',
    preparedBy: 'M. Alvarez',
    preparedAt: '2026-09-02',
    reviewedBy: 'S. Iyer',
    reviewedAt: '2026-09-03',
    approvedBy: 'S. Iyer',
    approvedAt: '2026-09-04',
    publishedAt: '2026-09-04',
    photos: 8,
    documents: 2,
    notify: false,
    spendToDate: 998_100,
    variance: -12_600,
    body: 'Two work fronts opened alongside the finishes on the upper floor. Materials were delivered and checked against the invoice before anything was posted.',
  },
  {
    id: 'pub-lv-10',
    projectId: 'lakeview',
    title: 'Rough-in complete and enclosure scheduled',
    period: 'October 2026',
    status: 'in-review',
    preparedBy: 'M. Alvarez',
    preparedAt: '2026-10-02',
    reviewedBy: null,
    reviewedAt: null,
    approvedBy: null,
    approvedAt: null,
    publishedAt: null,
    photos: 14,
    documents: 4,
    notify: true,
    spendToDate: 1_121_300,
    variance: -19_800,
    body: 'Draft — awaiting financial verification of the HVAC invoice before this is shown to investors. The equipment has been invoiced but not yet delivered.',
  },
  {
    id: 'pub-lv-11',
    projectId: 'lakeview',
    title: 'Cabinet package released to the shop',
    period: 'October 2026',
    status: 'draft',
    preparedBy: 'D. Nakamura',
    preparedAt: '2026-10-05',
    reviewedBy: null,
    reviewedAt: null,
    approvedBy: null,
    approvedAt: null,
    publishedAt: null,
    photos: 5,
    documents: 2,
    notify: false,
    spendToDate: 1_121_300,
    variance: -19_800,
    body: 'Draft — shop drawings attached and awaiting a lead-time confirmation before the narrative is written.',
  },
  {
    id: 'pub-mc-06',
    projectId: 'maple-court',
    title: 'Trusses delivered and roof dried in',
    period: 'October 2026',
    status: 'approved',
    preparedBy: 'P. Osei',
    preparedAt: '2026-10-24',
    reviewedBy: 'S. Iyer',
    reviewedAt: '2026-10-26',
    approvedBy: 'R. Campos',
    approvedAt: '2026-10-28',
    publishedAt: null,
    photos: 11,
    documents: 2,
    notify: true,
    spendToDate: 702_000,
    variance: -8_900,
    body: 'Approved and ready to publish. The roof is dried in and the enclosure phase can begin ahead of the forecast.',
  },
  {
    id: 'pub-mc-05',
    projectId: 'maple-court',
    title: 'Foundation poured and cured',
    period: 'August 2026',
    status: 'published',
    preparedBy: 'P. Osei',
    preparedAt: '2026-08-11',
    reviewedBy: 'S. Iyer',
    reviewedAt: '2026-08-12',
    approvedBy: 'R. Campos',
    approvedAt: '2026-08-14',
    publishedAt: '2026-08-14',
    photos: 9,
    documents: 2,
    notify: true,
    spendToDate: 577_000,
    variance: -6_700,
    body: 'The foundation passed inspection on the first attempt and cured within the planned window.',
  },
  {
    id: 'pub-mc-07',
    projectId: 'maple-court',
    title: 'Framing progress and second-floor deck',
    period: 'September 2026',
    status: 'published',
    preparedBy: 'P. Osei',
    preparedAt: '2026-09-27',
    reviewedBy: 'S. Iyer',
    reviewedAt: '2026-09-29',
    approvedBy: 'S. Iyer',
    approvedAt: '2026-09-30',
    publishedAt: '2026-09-30',
    photos: 7,
    documents: 1,
    notify: false,
    spendToDate: 664_400,
    variance: -7_400,
    body: 'The second-floor deck is set and framing continues on schedule. Two fronts are open at once.',
  },
  {
    id: 'pub-bh-02',
    projectId: 'birch-hollow',
    title: 'Site mobilized and erosion controls in place',
    period: 'September 2026',
    status: 'published',
    preparedBy: 'P. Osei',
    preparedAt: '2026-09-08',
    reviewedBy: 'S. Iyer',
    reviewedAt: '2026-09-09',
    approvedBy: 'R. Campos',
    approvedAt: '2026-09-11',
    publishedAt: '2026-09-11',
    photos: 6,
    documents: 2,
    notify: false,
    spendToDate: 96_000,
    variance: 4_100,
    body: 'The site is mobilized and erosion controls are installed ahead of the demolition phase.',
  },
  {
    id: 'pub-bh-03',
    projectId: 'birch-hollow',
    title: 'Demolition scheduled',
    period: 'November 2026',
    status: 'draft',
    preparedBy: 'P. Osei',
    preparedAt: '2026-10-01',
    reviewedBy: null,
    reviewedAt: null,
    approvedBy: null,
    approvedAt: null,
    publishedAt: null,
    photos: 2,
    documents: 1,
    notify: false,
    spendToDate: 96_000,
    variance: 4_100,
    body: 'Draft — waiting on the demolition contractor confirmation before the date is published.',
  },
];

/* ---- Opportunity pipeline — the document's §6 lifecycle ------------------ */

export type LifecycleStage =
  | 'opportunity'
  | 'viability'
  | 'capital-raising'
  | 'acquisition'
  | 'construction'
  | 'sale-and-close';

export interface Opportunity {
  id: string;
  name: string;
  address: string;
  city: string;
  stage: LifecycleStage;
  /** Land plus build, the figure the viability study works from. */
  projectedCost: number;
  projectedSale: number;
  projectedStart: string;
  owner: string;
  /** Percent of the capital target that is soft-committed, for stages that raise. */
  capitalSecured: number;
  capitalTarget: number;
  updatedAt: string;
  note: string;
}

export const OPPORTUNITIES: Opportunity[] = [
  { id: 'op-1', name: 'Cypress Bend', address: '2210 Cypress Bend Cove', city: 'Austin, TX', stage: 'opportunity', projectedCost: 1_150_000, projectedSale: 1_820_000, projectedStart: '2027-04-01', owner: 'R. Campos', capitalSecured: 0, capitalTarget: 760_000, updatedAt: '2026-09-20', note: 'Lot under option until 15 November. Soil study ordered.' },
  { id: 'op-2', name: 'Hillcrest Terrace', address: '540 Hillcrest Terrace', city: 'Lakeway, TX', stage: 'viability', projectedCost: 1_680_000, projectedSale: 2_640_000, projectedStart: '2027-06-01', owner: 'S. Iyer', capitalSecured: 0, capitalTarget: 1_120_000, updatedAt: '2026-09-12', note: 'Two massing options priced. Awaiting the civil engineer on drainage.' },
  { id: 'op-3', name: 'Barton Creek Lot 9', address: '9 Barton Creek Crossing', city: 'West Lake Hills, TX', stage: 'capital-raising', projectedCost: 2_240_000, projectedSale: 3_600_000, projectedStart: '2027-02-01', owner: 'R. Campos', capitalSecured: 780_000, capitalTarget: 1_500_000, updatedAt: '2026-09-19', note: 'Vantage Ridge Partners reviewing the pack. Marlow Capital has expressed interest.' },
  { id: 'op-4', name: 'Old Mill Road', address: '118 Old Mill Road', city: 'Dripping Springs, TX', stage: 'acquisition', projectedCost: 1_020_000, projectedSale: 1_690_000, projectedStart: '2026-12-01', owner: 'S. Iyer', capitalSecured: 690_000, capitalTarget: 690_000, updatedAt: '2026-09-22', note: 'Fully committed. Closing scheduled; title work complete.' },
  { id: 'op-5', name: 'Prairie Wind Farmhouse', address: '77 Prairie Wind Lane', city: 'Hutto, TX', stage: 'construction', projectedCost: 890_000, projectedSale: 1_450_000, projectedStart: '2026-10-15', owner: 'P. Osei', capitalSecured: 610_000, capitalTarget: 610_000, updatedAt: '2026-09-23', note: 'Permit issued. Mobilizing; not yet migrated into the platform as a live project.' },
  { id: 'op-6', name: 'Cedar Ridge Duplex', address: '310 Cedar Ridge Drive', city: 'Cedar Park, TX', stage: 'viability', projectedCost: 980_000, projectedSale: 1_520_000, projectedStart: '2027-08-01', owner: 'S. Iyer', capitalSecured: 0, capitalTarget: 650_000, updatedAt: '2026-08-30', note: 'Zoning for a duplex confirmed. Pro forma drafted.' },
];

/* ---- Reports — §13 and §16 ---------------------------------------------- */

export interface ReportRecord {
  id: string;
  projectId: string;
  kind: 'period' | 'phase' | 'final';
  title: string;
  scope: string;
  period: string;
  generatedAt: string;
  generatedBy: string;
  pages: number;
  published: boolean;
}

export const REPORTS: ReportRecord[] = [
  { id: 'rep-1', projectId: 'lakeview', kind: 'period', title: 'Lakeview Residence — September 2026', scope: 'Period', period: 'September 2026', generatedAt: '2026-09-18', generatedBy: 'S. Iyer', pages: 14, published: true },
  { id: 'rep-2', projectId: 'lakeview', kind: 'phase', title: 'Lakeview Residence — Framing and roofing', scope: 'Phase', period: 'Mar – Sep 2026', generatedAt: '2026-09-19', generatedBy: 'S. Iyer', pages: 22, published: true },
  { id: 'rep-3', projectId: 'lakeview', kind: 'period', title: 'Lakeview Residence — August 2026', scope: 'Period', period: 'August 2026', generatedAt: '2026-08-21', generatedBy: 'M. Alvarez', pages: 12, published: true },
  { id: 'rep-4', projectId: 'maple-court', kind: 'period', title: 'Maple Court Residence — September 2026', scope: 'Period', period: 'September 2026', generatedAt: '2026-09-30', generatedBy: 'P. Osei', pages: 11, published: true },
  { id: 'rep-5', projectId: 'maple-court', kind: 'phase', title: 'Maple Court Residence — Foundation', scope: 'Phase', period: 'May – Aug 2026', generatedAt: '2026-08-15', generatedBy: 'P. Osei', pages: 16, published: true },
  { id: 'rep-6', projectId: 'birch-hollow', kind: 'period', title: 'Birch Hollow Residence — September 2026', scope: 'Period', period: 'September 2026', generatedAt: '2026-09-11', generatedBy: 'P. Osei', pages: 8, published: true },
  { id: 'rep-7', projectId: 'lakeview', kind: 'final', title: 'Lakeview Residence — final report', scope: 'Whole project', period: 'Whole project', generatedAt: '—', generatedBy: '—', pages: 0, published: false },
];

/* ---- Access log — RF11, marked "a validar" in the document ------------- */

export interface AccessRecord {
  id: string;
  projectId: string;
  investorId: string;
  publicationId: string;
  viewedAt: string;
  /** How much of the published update was read, as a percentage. */
  depth: number;
}

export const ACCESS_LOG: AccessRecord[] = [
  { id: 'acc-1', projectId: 'lakeview', investorId: 'marlow', publicationId: 'pub-lv-09', viewedAt: '2026-09-18T14:22:00Z', depth: 100 },
  { id: 'acc-2', projectId: 'lakeview', investorId: 'prado', publicationId: 'pub-lv-09', viewedAt: '2026-09-18T19:41:00Z', depth: 82 },
  { id: 'acc-3', projectId: 'lakeview', investorId: 'whitfield', publicationId: 'pub-lv-09', viewedAt: '2026-09-19T08:03:00Z', depth: 100 },
  { id: 'acc-4', projectId: 'lakeview', investorId: 'sunfield', publicationId: 'pub-lv-09', viewedAt: '2026-09-21T11:17:00Z', depth: 45 },
  { id: 'acc-5', projectId: 'maple-court', investorId: 'marlow', publicationId: 'pub-mc-07', viewedAt: '2026-09-30T16:55:00Z', depth: 100 },
  { id: 'acc-6', projectId: 'maple-court', investorId: 'prado', publicationId: 'pub-mc-07', viewedAt: '2026-10-01T07:28:00Z', depth: 67 },
  { id: 'acc-7', projectId: 'birch-hollow', investorId: 'sunfield', publicationId: 'pub-bh-02', viewedAt: '2026-09-11T18:04:00Z', depth: 100 },
  { id: 'acc-8', projectId: 'birch-hollow', investorId: 'northgate', publicationId: 'pub-bh-02', viewedAt: '2026-09-12T09:12:00Z', depth: 91 },
];

/* ---- Derived helpers ----------------------------------------------------- */

export const findProject = (id: string | undefined) => PROJECTS.find((p) => p.id === id);
export const findInvestor = (id: string | undefined) => INVESTORS.find((i) => i.id === id);
export const participationsOf = (projectId: string) =>
  PARTICIPATIONS.filter((p) => p.projectId === projectId && p.sharePercent > 0);
export const participationsForInvestor = (investorId: string) =>
  PARTICIPATIONS.filter((p) => p.investorId === investorId && p.sharePercent > 0);
export const expensesOf = (projectId: string) => EXPENSES.filter((e) => e.projectId === projectId);
export const evidenceOf = (projectId: string) => EVIDENCE.filter((e) => e.projectId === projectId);
export const documentsOf = (projectId: string) => DOCUMENTS.filter((d) => d.projectId === projectId);
export const publicationsOf = (projectId: string) => PUBLICATIONS.filter((p) => p.projectId === projectId);
export const milestonesOf = (projectId: string) => MILESTONES.filter((m) => m.projectId === projectId);
export const scheduleOf = (projectId: string) => SCHEDULES[projectId] ?? [];

/** Published only — the gate every investor-facing screen must go through. */
export const publishedPublications = (projectId?: string) =>
  PUBLICATIONS.filter((p) => p.status === 'published' && (!projectId || p.projectId === projectId));
export const publishedDocuments = (projectId?: string) =>
  DOCUMENTS.filter((d) => d.published && d.status === 'verified' && (!projectId || d.projectId === projectId));
export const publishedEvidence = (projectId?: string) =>
  EVIDENCE.filter((e) => e.published && (!projectId || e.projectId === projectId));

/**
 * Money briefing figures, all derived so no screen can disagree with another.
 *
 * `variance` and `varianceStarted` answer different questions and the difference
 * matters. `variance` compares total spend against the TOTAL phased budget,
 * which on a project 68% complete necessarily reads as a large underspend —
 * it is arithmetic on work that has not been ordered yet, not a saving.
 * `varianceStarted` compares spend against the budget for the phases that have
 * actually begun, which is the figure a client reads as "are we over or under
 * on the work in progress". The shell headlines the second and keeps the first
 * available for the screens that want the whole-life view.
 */
export function projectTotals(projectId: string) {
  const project = findProject(projectId);
  const schedule = scheduleOf(projectId);
  const participations = participationsOf(projectId);

  const budgeted = schedule.reduce((sum, phase) => sum + phase.budgeted, 0);
  const actual = schedule.reduce((sum, phase) => sum + phase.actual, 0);

  const started = schedule.filter((phase) => phase.status !== 'planned');
  const startedBudgeted = started.reduce((sum, phase) => sum + phase.budgeted, 0);
  const startedActual = started.reduce((sum, phase) => sum + phase.actual, 0);

  const committed = participations.reduce((sum, entry) => sum + entry.committed, 0);
  const contributed = participations.reduce((sum, entry) => sum + entry.contributed, 0);

  return {
    budgeted,
    actual,
    /**
     * Whole-life variance. Negative means the phased budget exceeds spend, which
     * on an unfinished project is expected rather than good news — do not
     * headline it without saying which phases it covers.
     */
    variance: actual - budgeted,
    /** Budget and variance for work in progress only — the comparable figures. */
    startedBudgeted,
    startedActual,
    varianceStarted: startedActual - startedBudgeted,
    /** Sum of what investors have promised, which exceeds what they have paid. */
    committed,
    contributed,
    /** Promised but not yet called — the gap the finance team chases. */
    uncalled: committed - contributed,
    balance: project?.balance ?? 0,
    progress: project?.progress ?? 0,
    /** Contributed-but-unspent, the figure an investor actually asks about. */
    available: contributed - actual,
  };
}
